import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  Chip,
} from '@mui/material';
import { Add, Edit, Delete, Business } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';

interface Company {
  id: number;
  name: string;
  industry: string;
  description?: string;
  target_audience?: string;
  brand_voice?: string;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { companies, addCompany, updateCompany, selectCompany } = useCompany();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    industry: '',
    description: '',
    target_audience: '',
    brand_voice: '',
  });

  const handleOpenDialog = (company?: Company) => {
    if (company) {
      setEditingCompany(company);
      setCompanyForm({
        name: company.name,
        industry: company.industry,
        description: company.description || '',
        target_audience: company.target_audience || '',
        brand_voice: company.brand_voice || '',
      });
    } else {
      setEditingCompany(null);
      setCompanyForm({
        name: '',
        industry: '',
        description: '',
        target_audience: '',
        brand_voice: '',
      });
    }
    setOpenDialog(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCompany(null);
  };

  const handleSaveCompany = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!companyForm.name || !companyForm.industry) {
        setError('Company name and industry are required');
        return;
      }

      if (editingCompany) {
        await updateCompany(editingCompany.id, companyForm);
        setSuccess('Company updated successfully');
      } else {
        await addCompany(companyForm);
        setSuccess('Company added successfully');
      }

      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save company');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Company Management Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Your Companies
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
              >
                Add Company
              </Button>
            </Box>

            {companies.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No companies yet. Add your first company to get started!
              </Typography>
            ) : (
              <List>
                {companies.map((company) => (
                  <ListItem
                    key={company.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                    }}
                    secondaryAction={
                      <Box>
                        <IconButton edge="end" onClick={() => handleOpenDialog(company)}>
                          <Edit />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Business />
                          {company.name}
                        </Box>
                      }
                      secondary={
                        <>
                          <Chip label={company.industry} size="small" sx={{ mt: 0.5 }} />
                          {company.description && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {company.description}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Profile Settings
            </Typography>
            
            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Name"
                value={user?.name || ''}
                margin="normal"
                disabled
              />
              <TextField
                fullWidth
                label="Email"
                value={user?.email || ''}
                margin="normal"
                disabled
              />
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>
                Change Password
              </Typography>
              
              <TextField
                fullWidth
                label="Current Password"
                type="password"
                margin="normal"
              />
              <TextField
                fullWidth
                label="New Password"
                type="password"
                margin="normal"
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                margin="normal"
              />
              
              <Box sx={{ mt: 3 }}>
                <Button variant="contained" sx={{ mr: 2 }}>
                  Save Changes
                </Button>
                <Button variant="outlined">
                  Cancel
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your account settings and preferences.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Add/Edit Company Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCompany ? 'Edit Company' : 'Add New Company'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Company Name"
              fullWidth
              required
              value={companyForm.name}
              onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
              placeholder="e.g., Acme Corporation"
            />
            <TextField
              label="Industry"
              fullWidth
              required
              value={companyForm.industry}
              onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })}
              placeholder="e.g., Technology, Healthcare, Finance"
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={companyForm.description}
              onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })}
              placeholder="Brief description of your company"
            />
            <TextField
              label="Target Audience"
              fullWidth
              value={companyForm.target_audience}
              onChange={(e) => setCompanyForm({ ...companyForm, target_audience: e.target.value })}
              placeholder="e.g., Small business owners, Enterprise clients"
            />
            <TextField
              label="Brand Voice"
              fullWidth
              value={companyForm.brand_voice}
              onChange={(e) => setCompanyForm({ ...companyForm, brand_voice: e.target.value })}
              placeholder="e.g., Professional, Friendly, Authoritative"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveCompany} variant="contained" color="primary">
            {editingCompany ? 'Update' : 'Add'} Company
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;

